import BaseInteraction from './BaseInteraction';
import ContentItem from '../api/content/ContentItem';
import logger from '../log';
import { HLS, HLSjsConfigWrapper } from '@kollective-frontend/ksdk-api';
import { SourceType } from '../common/constants';
import bindHLSjsToWebRTC, {
  bindHLSjsConfigToWebRTC
} from '../webrtc/bindHLSjsToWebRTC';
import { Types, checkType, logUnableToSetupMessage } from '../util/checkTypes';
import attachHlsEvents from './utils/attachHlsEvents';
import { SourceSetEventOptions } from '../events';
import { dispatchInfo } from '../events/dispatch';
import { VoidMethod } from './interfaces';

const log = logger.getLogger(__filename);

export default class HLSjsConfigInteraction extends BaseInteraction<HTMLVideoElement> {
  wrapper: HLSjsConfigWrapper;
  hlsCleanupMethod?: VoidMethod;

  constructor(data: HLSjsConfigWrapper) {
    super(data.element);
    // We don't throw here as that would cascade out and crash the plugin.
    checkType(data, Types.HLSWrapper).emitErrorIfNotCorrectType();
    this.wrapper = data;
  }

  resetEventsAndInterceptors(
    // Actually should be HLSjsConfigWrapper but that gives an error.
    opts: SourceSetEventOptions<HTMLMediaElement>
  ): void {
    if (!opts.options) {
      this.resetMediaElement(null);
      return;
    }
    const { player, hls } = opts.options;
    if (player) {
      const wrapper = player as unknown as HLSjsConfigWrapper;
      this.resetMediaElement(wrapper.element);
      bindHLSjsConfigToWebRTC(wrapper, this.binder);
    }
    if (hls) {
      this.bindHls(hls);
    } else if (!player) {
      const msg =
        'Unable to re-bind hls, no hls object passed in sourceSet call';
      dispatchInfo(msg);
      log.warn(msg);
    }
    if (opts.options.ele) {
      const ele = opts.options?.ele as HTMLVideoElement;
      this.resetMediaElement(ele);
    } else if (!player) {
      const msg =
        'Unable to bind new media element, no media element passed to sourceSet call';
      dispatchInfo(msg);
      log.warn(msg);
    }
  }

  bindHls(hls: HLS): void {
    if (this.hlsCleanupMethod) {
      this.hlsCleanupMethod();
    }
    this.hlsCleanupMethod = attachHlsEvents(hls);
    bindHLSjsToWebRTC(hls, this.binder);
  }

  initialize(): void {
    super.initialize();
    const result = checkType(this.wrapper, Types.HLSWrapper);
    if (result.isType) {
      bindHLSjsConfigToWebRTC(this.wrapper, this.binder);
    } else {
      logUnableToSetupMessage(Types.HLSWrapper.name, log, result);
    }
  }

  loader(items: ContentItem[]): ContentItem[] {
    // We don't set source for this interaction (the integrator does).
    return items;
  }

  clearSource(): void {
    // We don't do anything here the integrator handles this.
  }

  mediaElement(): HTMLMediaElement {
    return this.player;
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.HLS];
  }

  addEventListeners(): void {
    super.addEventListeners();
    this.cleanupMethods.push(attachHlsEvents(this.wrapper));
  }
}
